/**
 * Level section component displaying a group of onboarding steps
 * Shows level header, steps grid, or locked teaser preview
 */

import React from "react";
import { Box, Flex, Paper, Stack, Text } from "@mantine/core";
import { IconLock } from "@tabler/icons-react";
import type { OnboardingStepItem } from "../types";
import { getLevelConfig, type LevelType } from "../utils/levelUtils";
import LevelBadge from "./LevelBadge";
import OnboardingStepCard from "./OnboardingStepCard";

interface LevelSectionProps {
  level: LevelType;
  steps: OnboardingStepItem[];
  isLocked: boolean;
  onStepClick: (stepId: string) => void;
  loadingStepId: string | null;
}

/**
 * Level section component with header and steps
 */
const LevelSection: React.FC<LevelSectionProps> = ({
  level,
  steps,
  isLocked,
  onStepClick,
  loadingStepId,
}) => {
  const config = getLevelConfig(level);
  const LevelIcon = config.icon;

  // Check if all steps in this level are complete
  const isLevelComplete = steps.every((step) => step.status === "complete");

  // Get lighter version of level color for border
  const getLevelBorderColor = () => {
    // If level is complete, use green border
    if (isLevelComplete) {
      return "#51cf66";
    }
    // Add 40% opacity to the level color for a lighter border
    const hex = config.color.replace("#", "");
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, 0.4)`;
  };

  // Previous level name for unlock message
  const getPreviousLevelName = () => {
    if (level === "advanced") return "Beginner";
    if (level === "elite") return "Advanced";
    return "";
  };

  return (
    <Box
      style={{
        position: "relative",
        maxWidth: 1000,
        marginInline: "auto",
        overflow: "visible",
      }}
    >
      <Paper
        shadow="sm"
        radius="lg"
        style={{
          position: "relative",
          overflow: "visible",
          backgroundColor: isLocked ? "#f8f9fa" : "#ffffff",
          border: `1px solid ${isLocked ? "#e9ecef" : getLevelBorderColor()}`,
          opacity: isLocked ? 0.7 : 1,
          transition: "all 0.3s ease",
          padding: "12px 16px 16px 16px",
        }}
      >
        <Stack gap="md" style={{ position: "relative", zIndex: 1 }}>
          {/* First Row: Icon and Badge */}
          <Flex align="center" gap="md">
            {/* Level Icon */}
            <Box
              style={{
                width: 28,
                height: 28,
                borderRadius: "6px",
                background: isLocked ? "#e0e0e0" : config.gradient,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: isLocked ? "none" : "0 2px 8px rgba(0, 0, 0, 0.12)",
              }}
            >
              {isLocked ? (
                <IconLock size={14} color="#666" />
              ) : (
                <LevelIcon size={14} color="white" stroke={1.5} />
              )}
            </Box>

            {/* Level Badge with Title */}
            <LevelBadge level={level} size="sm" customLabel={config.name} />
          </Flex>

          {/* Second Row: Steps Content */}
          <Box style={{ width: "100%" }}>
            {isLocked ? (
              /* Locked Teaser Preview */
              <Paper
                p="xl"
                radius="md"
                style={{
                  backgroundColor: "#f8f9fa",
                  border: "2px dashed #dee2e6",
                  textAlign: "center",
                  minHeight: 200,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Stack align="center" gap="md">
                  <IconLock size={40} color="#adb5bd" />
                  <Box>
                    <Text size="lg" fw={600} c="dimmed" mb="xs">
                      Level Locked
                    </Text>
                    <Text size="sm" c="dimmed">
                      Complete {getPreviousLevelName()} level to unlock{" "}
                      {steps.length} step{steps.length !== 1 ? "s" : ""}
                    </Text>
                  </Box>
                </Stack>
              </Paper>
            ) : (
              /* Unlocked Steps Grid - Only show incomplete steps */
              <>
                {(() => {
                  // Filter out completed steps - they'll appear in CompletedStepsSection
                  const incompleteSteps = steps.filter(
                    (step) => step.status !== "complete"
                  );

                  if (incompleteSteps.length > 0) {
                    return (
                      <Flex wrap="wrap" gap="md" justify="center">
                        {incompleteSteps.map((step) => (
                          <OnboardingStepCard
                            key={step.id}
                            step={step}
                            level={level}
                            onStepClick={onStepClick}
                            loading={loadingStepId === step.id}
                          />
                        ))}
                      </Flex>
                    );
                  } else {
                    return (
                      <Paper
                        p="xl"
                        radius="md"
                        style={{
                          backgroundColor: "#f8f9fa",
                          textAlign: "center",
                        }}
                      >
                        <Text size="sm" c="dimmed">
                          No active steps in this level
                        </Text>
                      </Paper>
                    );
                  }
                })()}
              </>
            )}
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
};

export default LevelSection;
